import React, { useContext, useRef, useState } from 'react';
import { Menu, MenuItem } from 'components/Menu';
import styled from 'styled-components';
import type { ILinkedUser } from '@/app-package';
import { Icon, Button, Text } from '@bb/elements-web';
import { Dropdown, message, Spin } from 'antd';
import { AuthContext } from 'contexts';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { DIALOG_KEYS } from 'modals/keys';
import LinkAccountModal from 'modals/LinkAccountModal/LinkAccountModal';
import { useBreakpoint } from 'hooks';
import { switchAccount } from 'api/UserController';
import { useMutation } from '@tanstack/react-query';

const AccountLinker = () => {
  const [isSwitching, setIsSwitching] = useState(false);
  const { md } = useBreakpoint();
  const { auth } = useContext(AuthContext);
  const { id: currentId, linkedAccounts } = auth || {};
  const containerRef = useRef(null);
  const { addDialog } = useDialogStore();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const { mutate: switchAccountMutation } = useMutation({
    mutationFn: switchAccount,
    onSuccess: () => window.location.reload(),
    onError: () => {
      setIsSwitching(false);
      message.error(
        'Unable to switch accounts, if the issue persists please contact support.',
      );
    },
  });

  const onLinkAccount = () => {
    setDropdownOpen(false);
    addDialog({
      component: <LinkAccountModal key={DIALOG_KEYS.LinkAccountModal} />,
      key: DIALOG_KEYS.LinkAccountModal,
    });
  };

  const getSubTitle = (account: ILinkedUser) => {
    if (!account) return '--';
    const { mlsNumber, officeId } = account;
    if (mlsNumber) return `Member ID: ${account.mlsNumber}`;
    if (officeId) return `Office ID: ${account.officeId}`;
    return '--';
  };

  return (
    <StyledContainer ref={containerRef}>
      <Dropdown
        getPopupContainer={() => containerRef?.current}
        placement="bottomRight"
        disabled={isSwitching}
        trigger={['click']}
        dropdownRender={() => (
          <StyledMenu selectable={false}>
            {linkedAccounts?.map(account => (
              <StyledMenuItem
                key={`${account.board}-${account?.officeId ?? account?.mlsNumber}`}
                childrenWrapperStyles={{ flex: 1 }}
                onClick={() => {
                  if (currentId !== account.id && !isSwitching) {
                    setIsSwitching(true);
                    switchAccountMutation(account.id);
                  }
                }}
                disabled={isSwitching}
              >
                <StyledMenuContent>
                  <StyledFlex>
                    <StyledTitle style={{ lineHeight: 1.1 }}>
                      {account.board}
                    </StyledTitle>
                    <StyledSubTitle style={{ lineHeight: 1.1 }}>
                      {getSubTitle(account)}
                    </StyledSubTitle>
                  </StyledFlex>
                  {currentId === account.id && !isSwitching && (
                    <Icon icon="checkOutlined" color="primary" />
                  )}
                </StyledMenuContent>
              </StyledMenuItem>
            ))}

            <StyledMenuItem
              key="link-another-account-menu-item"
              onClick={onLinkAccount}
              disabled={isSwitching}
            >
              <StyledButtonContainer>
                <Icon icon="plusCircleOutlined" color="primary" />
                <StyledLink color="primary">Link another account</StyledLink>
              </StyledButtonContainer>
            </StyledMenuItem>
          </StyledMenu>
        )}
      >
        <StyledDropdownButton
          id="dropdown-link-account"
          type="primary"
          color="white"
          dataCy="dropdown-link-account"
          disabled={isSwitching}
          onClick={() => setDropdownOpen(prev => !prev)}
        >
          <StyledInner $md={md}>
            <StyledTitle ellipsis style={{ lineHeight: 1.1 }}>
              {linkedAccounts[0]?.board}
            </StyledTitle>
            <StyledSubTitle ellipsis style={{ lineHeight: 1.1 }}>
              {getSubTitle(linkedAccounts[0])}
            </StyledSubTitle>
          </StyledInner>
          {isSwitching ? (
            <StyledSpin size="small" />
          ) : (
            <Icon
              color="black"
              icon={dropdownOpen ? 'arrowUpOutlined' : 'arrowDownOutlined'}
            />
          )}
        </StyledDropdownButton>
      </Dropdown>
    </StyledContainer>
  );
};

export default AccountLinker;

const StyledInner = styled.div<{ $md?: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: ${({ $md }) => ($md ? '160px' : 'calc(100% - 15px)')};
`;

const StyledSpin = styled(Spin)`
  padding-top: 6px;
  padding-left: 10px;
`;

const StyledLink = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  justify-content: center;
`;

const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
  text-align: left;
`;

const StyledSubTitle = styled(Text)`
  font-size: 12px;
  line-height: 1.1;
  width: fit-content;
  color: #8b8b8b;
`;

const StyledMenuContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  width: 100%;
`;

const StyledMenu = styled(Menu)`
  && {
    padding: 10px 6px;
    width: 100%;
    max-height: 300px;
    position: absolute;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &&& {
    width: 100%;
    flex: 1;
  }
  &&& .ant-dropdown-menu-title-content > * {
    padding-left: 6px;
  }
`;

const StyledContainer = styled.div`
  max-width: 250px;
  max-height: 50px;
`;

const StyledDropdownButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    line-height: 30px;
    font-size: 14px;
    text-align: center;
    border-radius: 5px;
    font-weight: 400;
    min-width: auto;
    justify-content: space-between;
    color: black;
    overflow: hidden;
    width: 100%;
    background-color: white !important;
    color: black;
    height: 46px;
  }
`;
